import { Ref, useMemo } from "react";
import { MultiSelect as MantineMultiSelect, MultiSelectProps as MantineMultiSelectProps } from "@mantine/core";
import styles from "./styles.module.css";
import Loader from "../Loader";
import { TFunction } from "i18next";

export interface MultiSelectProps extends Omit<MantineMultiSelectProps, "value" | "onChange"> {
  className?: string;
  loading?: boolean;
  value?: string[] | any[];
  onChange?: (value: string[] | any[]) => void;
  valueKey?: string;
  returnObjects?: boolean;
  variant?: "filled";
  ref?: Ref<HTMLInputElement>;
}

function MultiSelect({
  loading,
  value,
  onChange,
  data = [],
  valueKey = "value",
  returnObjects = false,
  variant,
  ref,
  ...props
}: MultiSelectProps) {
  // Convert object values to string values for Mantine MultiSelect
  const stringValue = useMemo(() => {
    if (!value) return [];

    if (returnObjects && Array.isArray(value)) {
      // If we're expecting objects, convert them to strings using valueKey
      return value.map((item) => (typeof item === "object" ? String(item[valueKey]) : String(item)));
    }

    return Array.isArray(value) ? value.map(String) : [];
  }, [value, valueKey, returnObjects]);

  const handleChange = (selectedValues: string[]) => {
    if (!onChange) return;

    if (returnObjects && Array.isArray(data)) {
      // Convert string values back to objects
      const selectedObjects = selectedValues.map((selectedValue) => {
        const dataItem = data.find((item) => {
          if (typeof item === "object" && "value" in item) {
            return String(item.value) === selectedValue;
          }
          return false;
        });

        // Return the original object if found, otherwise create a simple object
        if (dataItem && typeof dataItem === "object" && "originalObject" in dataItem) {
          return (dataItem as any).originalObject;
        }

        return { [valueKey]: selectedValue };
      });
      onChange(selectedObjects);
    } else {
      onChange(selectedValues);
    }
  };

  return (
    <MantineMultiSelect
      {...props}
      ref={ref}
      value={stringValue}
      onChange={handleChange}
      data={data}
      radius={0}
      searchable
      onBlur={props.onBlur}
      className={`${styles.selectRoot} ${props.className || ""}`}
      classNames={{
        label: styles.selectLabel,
        root: styles.selectRoot,
        input: `${styles.input} ${variant === "filled" ? styles.inputFilled : ""}`,
        pill: styles.selectPill,
      }}
      {...(loading ? { rightSection: <Loader size="xs" />, rightSectionWidth: 28 } : {})}
    />
  );
}

export default MultiSelect;

/**
 * Transforms an array of objects into a format for a select component, with optional translation.
 *
 * @param data - The input array of objects.
 * @param valueKey - The key to use for the `value` property.
 * @param labelKey - The key to use for the `label` property.
 * @param t - The translation function from your i18n library (optional).
 * @param prefix - The prefix for the translation key (e.g., 'product.formats').
 * @param uppercase - should display in uppercase
 * @returns An array of objects with `value` and `label` properties.
 */
export const transformDataToSelectOption = (
  data: any[],
  valueKey: string,
  labelKey: string,
  t?: TFunction,
  prefix?: string,
  uppercase?: boolean
) => {
  if (!data || !Array.isArray(data)) {
    return [];
  }

  return data.map((item) => {
    const label = item[labelKey];

    const translationKey = prefix ? `${prefix}.${label}` : label;

    const translatedLabel = t && prefix ? t(translationKey) : label;

    return {
      value: String(item[valueKey]),
      label: uppercase ? translatedLabel.toUpperCase() : translatedLabel,
      originalObject: item, // Store the original object for conversion back
    };
  });
};
